import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import Card from "react-bootstrap/Card";
import { FaUserShield, FaUser, FaShieldAlt, FaEllipsisV, FaBan, FaUserCheck, FaTrash } from "react-icons/fa";
import { isAdminLike } from "../services/utils/utiltyFunctions";
import "./UserCard.css";

interface UserCardProps {
  id: string;
  fullName: string;
  username: string;
  phoneNumber: string;
  role: string;
  // When true, this user is soft-disabled — backend will reject auth.
  // Card renders dimmed with a "Disabled" badge; menu shows "Re-enable"
  // instead of "Disable".
  disabled?: boolean;
  onEdit: (id: string) => void;
  // Hard delete (Cognito + DB). Backend only allows it for users with no
  // activity history — the menu item is always shown, but the server
  // refuses with a clear message for anyone who has actually used the
  // system (disable is the tool for those).
  onDelete: (id: string) => void;
  onResetMfa?: (id: string, fullName: string) => void;
  onToggleDisabled?: (id: string, fullName: string, currentlyDisabled: boolean) => void;
}

const UserCard: React.FC<UserCardProps> = ({
  id,
  fullName,
  username,
  phoneNumber,
  role,
  disabled = false,
  onEdit,
  onDelete,
  onResetMfa,
  onToggleDisabled,
}) => {
  // Treat superadmin the same as admin in org-scoped views.
  const adminish = isAdminLike(role);
  const roleIcon = adminish ? <FaUserShield /> : <FaUser />;

  // Gear menu — destructive admin actions live here so they don't dominate
  // the card and can't be clicked accidentally. Same pattern as the
  // ClaimedSystem gear menu: portaled to body, dismiss on outside click.
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (menuBtnRef.current?.contains(target)) return;
      if (target.closest?.(".uc-menu")) return;
      setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const openMenu = () => {
    if (menuBtnRef.current) {
      const rect = menuBtnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    }
    setMenuOpen((o) => !o);
  };

  // Header gradient is gray when disabled to make terminated users
  // unmistakable in a long list.
  const headerBackground = disabled
    ? 'linear-gradient(135deg, #94a3b8, #64748b)'
    : adminish
      ? 'linear-gradient(135deg, #10b981, #059669)'
      : 'linear-gradient(135deg, #2196F3, #1976D2)';

  return (
    <Card style={{
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      border: disabled ? '1px solid rgba(148, 163, 184, 0.4)' : '1px solid rgba(33, 150, 243, 0.1)',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      opacity: disabled ? 0.7 : 1,
    }}>
      <Card.Header style={{
        background: headerBackground,
        border: 'none',
        borderRadius: '16px 16px 0 0',
        padding: '16px 20px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{
          fontWeight: '600',
          fontSize: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          {fullName}
          {disabled && (
            <span
              title="This user is disabled and cannot sign in."
              style={{
                fontSize: '9px',
                fontWeight: 800,
                letterSpacing: '0.06em',
                padding: '2px 6px',
                borderRadius: '6px',
                background: 'rgba(255,255,255,0.25)',
                border: '1px solid rgba(255,255,255,0.4)',
              }}
            >
              DISABLED
            </span>
          )}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '18px' }}>{roleIcon}</span>
          {(onResetMfa || onToggleDisabled) && (
            <button
              ref={menuBtnRef}
              onClick={openMenu}
              aria-label="User actions"
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                color: 'white',
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              <FaEllipsisV size={12} />
            </button>
          )}
        </div>
      </Card.Header>
      <Card.Body style={{ padding: '20px' }}>
        <div style={{ marginBottom: '12px' }}>
          <span style={{
            fontSize: '11px',
            color: '#64748b',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>Username</span>
          <div style={{
            fontSize: '14px',
            color: '#1e293b',
            fontWeight: '500'
          }}>{username}</div>
        </div>
        <div>
          <span style={{
            fontSize: '11px',
            color: '#64748b',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>Phone</span>
          <div style={{
            fontSize: '14px',
            color: '#1e293b',
            fontWeight: '500'
          }}>{phoneNumber}</div>
        </div>
      </Card.Body>

      {menuOpen && (onResetMfa || onToggleDisabled) && ReactDOM.createPortal(
        <div
          className="uc-menu"
          style={{
            position: 'fixed',
            top: menuPos.top,
            right: menuPos.right,
            left: 'auto',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
            minWidth: '210px',
            padding: '6px',
            zIndex: 3000,
          }}
        >
          {onResetMfa && (
            <button
              onClick={() => {
                setMenuOpen(false);
                onResetMfa(id, fullName);
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px',
                background: 'transparent',
                border: 'none',
                borderRadius: '6px',
                color: '#92400e',
                fontSize: '13px',
                fontWeight: 600,
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#fffbeb'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <FaShieldAlt size={11} />
              Reset MFA
            </button>
          )}
          {onToggleDisabled && (
            <button
              onClick={() => {
                setMenuOpen(false);
                onToggleDisabled(id, fullName, disabled);
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px',
                background: 'transparent',
                border: 'none',
                borderRadius: '6px',
                color: disabled ? '#15803d' : '#b91c1c',
                fontSize: '13px',
                fontWeight: 600,
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = disabled ? '#f0fdf4' : '#fef2f2'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              {disabled ? <FaUserCheck size={11} /> : <FaBan size={11} />}
              {disabled ? 'Re-enable user' : 'Disable user'}
            </button>
          )}
          <div style={{ height: '1px', background: '#f1f5f9', margin: '4px 6px' }} />
          <button
            onClick={() => {
              setMenuOpen(false);
              onDelete(id);
            }}
            title="Permanently deletes the user. Only allowed for users with no activity history."
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              background: 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: '#991b1b',
              fontSize: '13px',
              fontWeight: 600,
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <FaTrash size={11} />
            Delete user…
          </button>
        </div>,
        document.body
      )}
    </Card>
  );
};

export default UserCard;
